import { ArrowRightIcon } from '@heroicons/react/outline';
import { ArrowLeftIcon } from '@heroicons/react/solid';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import ImageSlider, { IBookReward } from '../ImageSlider';
import SquareDesign from '../Square';
import { notify } from '../../shared/basic/notify';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { getAllProductsRewards } from '../../../services/Products';

interface Props {
  type: 'book' | 'course';
  setLoading: (value: boolean) => void;
  loading: boolean;
}

export interface ICourseRewards {
  _id: string;
  title: string;
  image: string;
  digitalProductId: string;
  coin_price: number;
}

const RewardSlider: React.FC<Props> = ({ type, setLoading, loading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [books, setBooks] = useState<
    {
      _id: string;
      title: string;
      image: string;
      digitalProductId: string;
      coin_price: number;
    }[]
  >([]);
  const screens = useBreakpoint();
  const autoSlideInterval = useRef<NodeJS.Timeout | null>(null);

  const [course, setCourse] = useState<ICourseRewards[]>([]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0
        ? (type === 'book' ? books.length : course.length) - 1
        : prevIndex - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === (type === 'book' ? books.length : course.length) - 1
        ? 0
        : prevIndex + 1,
    );
  };

  const handleGetAllBooks = async () => {
    try {
      setLoading(true);
      const response = await getAllProductsRewards(1, 8, 999);
      const { data } = response;

      const formattedBooks = data?.map((book: any) => ({
        _id: book?.id,
        image: book?.thumbnail_image,
        title: book?.name,
        digitalProductId: book?.id,
        coin_price: book?.coin_price,
      }));

      setBooks(formattedBooks);
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGetAllCourse = async () => {
    try {
      setLoading(true);
      const response = await getAllProductsRewards(1, 5, 500);

      const { data } = response;

      const formattedCourses = (data ?? response)?.map((course: any) => ({
        _id: course?.id,
        image: course?.thumbnail_image,
        title: course?.name,
        coin_price: course?.coin_price,
        digitalProductId: course?.id,
      }));
      setCourse(formattedCourses);
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (type === 'book') {
      handleGetAllBooks();
    }
    if (type === 'course') {
      handleGetAllCourse();
    }
  }, [type]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const renderItem = (
    item: IBookReward | ICourseRewards,
    className: string,
  ) => {
    if (type === 'book') {
      return (
        <div className='w-[100px] md:w-[136px] xl:w-[170px] h-[150px] md:h-[200px] xl:h-[250px] mx-6 bg-bgDarkGray rounded-lg'>
          <Image
            src={item.image}
            alt={item.title}
            width={170}
            height={250}
            className={className}
          />
        </div>
      );
    } else {
      const getWidth = () => {
        if (screens.xl) return 300;
        if (screens.lg) return 240;
        if (screens.md) return 200;
        return 160;
      };

      const width = getWidth();
      const height = width / 2;

      return (
        <div
          className='mx-6 bg-bgDarkGray rounded-lg relative'
          style={{
            height: `${height}px`,
            width: `${width}px`,
          }}
        >
          <Image
            src={item.image}
            alt={item.title}
            className={className}
            layout='fill'
            objectFit='contain'
          />
        </div>
      );
    }
  };
  useEffect(() => {
    const startAutoSlide = () => {
      autoSlideInterval.current = setInterval(handleNext, 3000);
    };

    const stopAutoSlide = () => {
      if (autoSlideInterval.current) {
        clearInterval(autoSlideInterval.current);
        autoSlideInterval.current = null;
      }
    };

    startAutoSlide();
    return () => stopAutoSlide();
  }, [type, currentIndex]);

  // Pause on hover
  const handleMouseEnter = () => {
    if (autoSlideInterval.current) {
      clearInterval(autoSlideInterval.current);
      autoSlideInterval.current = null;
    }
  };

  const handleMouseLeave = () => {
    autoSlideInterval.current = setInterval(handleNext, 3000);
  };
  const imageSliderPosition = useMemo(() => {
    const basePosition =
      'absolute left-1/2 transform -top-2/3 md:top-0 lg:top-1/3  -translate-x-1/2';

    const spacing = {
      book: {
        mt: [
          'mt-[1vh]',
          'md:-mt-[3vh] lgMd:-mt-[1vh] lg:-mt-[5vh] xl:-mt-[22%] 2xl:-mt-[21%] ',
        ],
        translateY: ['-translate-y-0 md:-translate-y-1/3', 'lg:translate-y-0'],
      },
      course: {
        mt: ['mt-[7vh]', 'md:mt-[0vh] lgMd:mt-[2vh] lg:-mt-0 xl:-mt-[2vh]'],
        translateY: ['-translate-y-0  md:-translate-y-1/3', 'lg:translate-y-0'],
      },
    };

    const typeSpacing = spacing[type];

    return `${basePosition} ${typeSpacing.mt.join(' ')} ${typeSpacing.translateY.join(' ')}`;
  }, [type]);
  return (
    <>
      <div
        className={clsx(
          type !== 'book' ? 'pb-28' : '',
          'flex flex-col items-center justify-center overflow-hidden relative w-full h-full -z-40 ',
        )}
      >
        <div
          className={clsx(
            type === 'book' ? 'top-0 xl:top-[10%]' : 'top-[2%] xl:top-[15%]',
            'absolute ',
          )}
        >
          <div className='flex justify-center items-center flex-col'>
            <span className='text-base text-primary font-semibold'>
              Redeem {type === 'book' ? 'Book' : 'Course'}
            </span>
            <span className='text-2xl xl:text-3xl font-semibold text-white'>
              Using Z-coins
            </span>
          </div>
        </div>
        <button
          onClick={handlePrev}
          className={clsx(
            type === 'book'
              ? ' xl:mt-[10vh] mt-0 md:mt-[13vh] lg:mt-0 -translate-y-1/2  xl:-translate-y-0'
              : '-mt-[4vh] md:mt-[5vh] xl:mt-[5vh] lg:mt-0 -translate-y-1/2',
            'absolute left-4 md:left-[25vw]  lg:left-[30vw] top-3/4 md:top-1/2 lg:top-3/4   transform  w-10 h-10 flex justify-center items-center z-10 bg-cartBgColor rounded-full hover:bg-bgDarkGray',
          )}
        >
          <ArrowLeftIcon className='text-sm w-5 h-5' />
        </button>
        <div className='relative flex h-[450px] md:h-[600px] lg:h-[70vh] xl:h-screen  justify-center items-end -z-50'>
          <span
            className={clsx(
              type === 'book'
                ? ' lgMd:translate-y-[6vh] lg:translate-y-0 '
                : 'translate-y-[7vh]  md:translate-y-[6vh] lg:translate-y-0 ',
              '',
              'relative',
            )}
          >
            <SquareDesign />
            <div className={imageSliderPosition}>
              <ShouldRender check={!loading}>
                <div
                  {...swipeHandlers}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <ImageSlider
                    handleNext={handleNext}
                    books={type === 'book' ? books : course}
                    currentIndex={currentIndex}
                    handlePrev={handlePrev}
                    renderItem={renderItem}
                    type={type === 'book' ? 'book' : 'course'}
                  />
                </div>
              </ShouldRender>
            </div>
          </span>
        </div>

        <button
          onClick={handleNext}
          className={clsx(
            type === 'book'
              ? ' xl:mt-[10vh] mt-0 md:mt-[13vh] lg:mt-0 -translate-y-1/2  xl:-translate-y-0'
              : '-mt-[4vh] md:mt-[5vh] xl:mt-[5vh] lg:mt-0 -translate-y-1/2',
            'absolute right-4 md:right-[25vw]   lg:right-[30vw] transform  top-3/4 md:top-1/2 lg:top-3/4  w-10 h-10 flex justify-center items-center z-10 bg-cartBgColor rounded-full hover:bg-bgDarkGray',
          )}
        >
          <ArrowRightIcon className='text-sm w-5 h-5' />
        </button>
      </div>
    </>
  );
};

export default RewardSlider;
